import React, { useEffect, useState } from "react";
import axios from "axios";
import swal from "sweetalert";

const empty = {
  canonical: "",
  sitemap_url: "",
  robots_url: "",
  indexing: false,
  google_site_verification: "",
  google_dns_verification: "",
  google_html_file: "",
  google_html_url: "",
  verification_saved: false,
  google_confirmed: false,
  api_connected: false,
};

export default function SearchConsole() {
  const [info, setInfo] = useState(empty);
  const [canonical, setCanonical] = useState("");
  const [token, setToken] = useState("");
  const [dns, setDns] = useState("");
  const [htmlFile, setHtmlFile] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => {
    axios.get("/api/admin/seo/search-console").then((res) => {
      if (res.data.status === 200) {
        setInfo(res.data);
        setCanonical(res.data.canonical || "");
        setToken(res.data.google_site_verification || "");
        setDns(res.data.google_dns_verification || "");
        setHtmlFile(res.data.google_html_file || "");
      }
    });
  };

  useEffect(() => {
    document.title = "Search Console — Admin";
    load();
  }, []);

  const save = (event) => {
    event.preventDefault();
    setSaving(true);
    axios
      .post("/api/admin/seo/search-console", {
        canonical_base_url: canonical,
        google_site_verification: token,
        google_dns_verification: dns,
        google_html_verification: htmlFile,
      })
      .then((res) => {
        if (res.data.status === 200) {
          setInfo(res.data);
          swal("Saved", "The verification code is stored for this store.", "success");
        }
      })
      .catch((err) => {
        const message = err.response?.data?.message || "The verification details could not be saved.";
        swal("Check the code", message, "warning");
      })
      .finally(() => setSaving(false));
  };

  return (
    <div className="container-fluid py-3">
      <div className="admin-card bg-white rounded-3 shadow-sm border mb-4">
        <div className="p-4 border-bottom">
          <h4 className="mb-1">Google Search Console</h4>
          <p className="text-muted mb-0">
            The sitemap is generated automatically. Submitting it to Google is a separate step.
          </p>
        </div>
        <div className="p-4">
          <div className="row g-3 mb-4">
            <div className="col-md-4">
              <div className="text-muted small">Canonical domain</div>
              <div className="fw-semibold">{info.canonical || "Set an https address below"}</div>
            </div>
            <div className="col-md-4">
              <div className="text-muted small">Sitemap</div>
              {info.sitemap_url ? (
                <a href={info.sitemap_url} target="_blank" rel="noreferrer">{info.sitemap_url}</a>
              ) : (
                <div>Available after the canonical URL is saved</div>
              )}
            </div>
            <div className="col-md-4">
              <div className="text-muted small">Verification</div>
              <div className="fw-semibold">
                {info.verification_saved
                  ? "Code saved. Google has not confirmed this property from here."
                  : "No code saved"}
              </div>
            </div>
          </div>

          <ol className="mb-4">
            <li>Open Google Search Console and add a property for the canonical domain.</li>
            <li>For the HTML file method, paste the file name below, such as google1234abcd.html, then save. Open the address and confirm the page is one line that starts with google-site-verification. Then click Verify in Google.</li>
            <li>For the HTML tag method, paste only the content value. For DNS, add the TXT record at the domain host. Saving the DNS value here does not create that record.</li>
            <li>When Google says the property is verified, open Sitemaps and submit the sitemap address.</li>
          </ol>

          <form onSubmit={save} className="d-flex flex-column gap-3">
            <label className="d-flex flex-column gap-1">
              <span className="fw-semibold">Canonical URL</span>
              <input
                className="form-control"
                value={canonical}
                onChange={(event) => setCanonical(event.target.value)}
                placeholder="https://example.com"
              />
            </label>
            <label className="d-flex flex-column gap-1">
              <span className="fw-semibold">HTML file name</span>
              <input
                className="form-control"
                value={htmlFile}
                onChange={(event) => setHtmlFile(event.target.value)}
                placeholder="google1234abcd.html"
                autoComplete="off"
              />
              <span className="text-muted small">
                {info.google_html_url
                  ? `Google must open ${info.google_html_url} and see only the verification line.`
                  : "Google looks for this file at the site root. The shop page cannot stand in for it."}
              </span>
            </label>
            <label className="d-flex flex-column gap-1">
              <span className="fw-semibold">HTML tag code</span>
              <input
                className="form-control"
                value={token}
                onChange={(event) => setToken(event.target.value)}
                placeholder="Paste the content value from the meta tag"
                autoComplete="off"
              />
            </label>
            <label className="d-flex flex-column gap-1">
              <span className="fw-semibold">DNS TXT value</span>
              <input
                className="form-control"
                value={dns}
                onChange={(event) => setDns(event.target.value)}
                placeholder="google-site-verification=..."
                autoComplete="off"
              />
            </label>
            <div>
              <button type="submit" className="btn btn-dark" disabled={saving}>
                {saving ? "Saving…" : "Save verification"}
              </button>
            </div>
          </form>

          <div className="border rounded-3 p-3 mt-4">
            <div className="fw-semibold mb-1">Search performance</div>
            <p className="text-muted mb-0">
              Clicks, impressions, CTR, and average position stay in Google Search Console.
              A direct connection needs a Google Cloud OAuth client on the server. This store
              is not connected to the Search Console API, and no Google password is stored.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
